import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { UserBiqData } from '@app/schemas/biq';
import { PlanCareerPathService } from '@app/services/api/plan-career-path/plan-career-path.service';
import { COURSE_MAJOR_CIP, MajorType } from '@app/utils/CIP-code';
import { MATCH_SCORE_IMG_SET, PLAN_NEXT_LIST, SKILLS_MAPPING } from '@app/utils/build-your-plan';
import { formatSalary } from '@app/utils/careers';
import { CAREER_GOAL_DETAILS_LOCAL_KEY } from '@app/utils/constants';
import { getGrowthStatementByCategory } from '@app/utils/growth-statement';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AddResumeDialogComponent, AddResumeDialogResult } from '@app/shared/results/add-resume-dialog/add-resume-dialog.component';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-plan-path',
  imports: [
    RouterLink,
  ],
  templateUrl: './plan-path.component.html',
  styleUrl: './plan-path.component.css'
})
export class PlanPathComponent implements OnInit {
  @Output() resumeUploaded = new EventEmitter<void>();
  public userData!: UserBiqData;
  private readonly courseMajorOptions: MajorType[] = COURSE_MAJOR_CIP;
  public userMajor!: MajorType | null;
  public skills: string[] = [];
  public skillGaps: string[] = [];
  public matchScoreImg: string = "";
  private readonly matchScoreImgSet = MATCH_SCORE_IMG_SET;
  public nextItemList = PLAN_NEXT_LIST;
  public isLoading: boolean = false;
  public loadingMessage: string = '';
  public isError: string = "";
  public planPathData: any;
  public careerGrowth: string = '';
  public careerTitle: string = '';
  public careerSalary: string = '';
  public careerGrowthState: string = '';
  public isUserDropdownOpen: boolean = false;
  public isGoalDropdownOpen: boolean = false;
  public careerGoalRank!: number;
  public careerGoalOnetCode!: string;
  public hasResume = false;

  constructor(
    private store: Store<{ userBiq: UserBiqData; resumeDetails: boolean }>,
    private planPathService: PlanCareerPathService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.store.select('userBiq').subscribe(value => {
      this.userData = value;
      this.userMajor = this.courseMajorOptions.find(e => e.value == value.major)!;
    });
    this.store.select('resumeDetails').subscribe(value => {
      this.hasResume = value;
    });
  }

  ngOnInit() {
    this.loadPlanPath();
  }

  loadPlanPath() {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.loadingMessage = 'Loading your career plan…';

    this.planPathService.getPlanPathSummary()
    .pipe(
      finalize(() => {
        this.isLoading = false;
        this.loadingMessage = this.isError ? 'Career plan failed to load.' : 'Career plan loaded.';
      })
    ).subscribe({
      next: (response) => {
        this.planPathData = response;
        this.processPathData();
      },
      error: (error) => {
        console.error('Error loading plan path:', error);
        this.isError = 'Error while fetching career path plan...';
      }
    });
  }

  processPathData() {
    const userCareer = this.planPathData.user_career;
    this.careerTitle = userCareer.onet_name;
    this.careerGrowthState = this.userData.futureLocation.split(",")?.[1]?.trim();
    this.careerGoalRank = userCareer.rank;
    this.careerGoalOnetCode = userCareer.onet_code;

    const veryHigh: string[] = [];
    const high: string[] = [];
    const medium: string[] = [];
    const low: string[] = [];

    const skillsSet = userCareer.match_report.skills.baseline.concat(
      userCareer.match_report.skills.technical
    );
    skillsSet.map((el: any) => {
      if (el.assessment_category == SKILLS_MAPPING.VERY_HIGH) {
        veryHigh.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.HIGH) {
        high.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.MEDIUM) {
        medium.push(el.skill_name);
      } else if (el.assessment_category == SKILLS_MAPPING.LOW) {
        low.push(el.skill_name);
      }
    });

    this.skills = veryHigh.length ? veryHigh : high;
    this.skillGaps = low.length ? low : medium;

    this.careerGrowth = getGrowthStatementByCategory(userCareer.pmp?.growth_category || null);
    this.careerSalary = formatSalary(userCareer.oews?.med_salary);
    
    // Track that the Plan Your Path section content has loaded
    analytics.track('Plan Path Available', {
      section: 'plan',
      career: this.careerGoalOnetCode,
      match_level: userCareer.match_score_category || '',
      aligned_skills: this.skills,
      skill_gap: this.skillGaps,
      path_advice: this.planPathData?.plan || {}
    });
  }

  toggleUserDropdown() {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
    this.isGoalDropdownOpen = false;
  }

  toggleGoalDropdown() {
    this.isGoalDropdownOpen = !this.isGoalDropdownOpen;
    this.isUserDropdownOpen = false;
  }
  
  onEditProfile() {
    analytics.track('Plan Profile Edit', { section: 'plan' });
  }

  onViewOccupationDetails() {
    analytics.track('Plan Goal View', { section: 'plan' });
  }

  onChangeWorkLocation() {
    analytics.track('Plan Goal Location Change', { section: 'plan' });
  }

  onUpdateCareerGoal() {
    analytics.track('Plan Goal Edit', { section: 'plan' });
  }
  
  get encodedCareerGoalOnetCode(): string {
    return this.careerGoalOnetCode ? encodeURIComponent(this.careerGoalOnetCode) : '';
  }

  openResumeDialog() {
    analytics.track('Plan Path View Resume', { section: 'plan' });
    const dialogRef = this.dialog.open(AddResumeDialogComponent, {
      panelClass: 'add-resume-dialog',
      width: '95%',
      maxWidth: '1632px',
      maxHeight: '95vh',
      data: { mode: 'view', redirectTo: '/build-plan', section: 'plan' },
    });

    dialogRef.afterClosed().subscribe((result?: AddResumeDialogResult) => {
      if (result?.resumeUploaded) {
        this.resumeUploaded.emit();
      }
    });
  }
}
